import { Component, signal } from '@angular/core';

@Component({
  selector: 'app-partners-resellers-page',
  template: `
    <!-- Hero -->
    <app-page-section backgroundColor="secondary" [waves]="true" [noContainer]="true">
      <div class="container max-w-4xl text-center">
        <div class="flex items-center justify-center gap-3 text-white/40">
          <span class="text-2xl font-light">/</span>
          <i class="fal fa-handshake text-white text-2xl"></i>
          <span class="text-2xl font-light">/</span>
        </div>
        <div class="mt-6 text-white">
          <h1 class="mt-6">Devenez revendeur et<br class="hidden sm:block" />générez un <span class="text-primary-500">revenu récurrent</span></h1>
          <p class="mt-6 text-base text-balance text-white/70">Que vous soyez client, indépendant ou revendeur IT — rejoignez le programme partenaire de location-salles.ch et touchez une commission sur chaque abonnement que vous apportez. Zéro frais d'entrée, zéro engagement.</p>
        </div>
        <div class="mt-12 flex flex-col gap-4 max-w-xl mx-auto">
          <a href="#deux-formules" (click)="scrollTo('deux-formules', $event); openFormula.set('apporteur')" class="flex items-end justify-between rounded-2xl bg-secondary-900 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] p-6 no-underline hover:bg-gray-900 hover:border-white/15 transition-colors group">
            <div class="text-left self-center">
              <p class="text-lg font-semibold text-white/80 group-hover:text-white transition-colors">Apporteur d'affaire</p>
              <p class="mt-1 text-sm text-white/40">min. CHF 560.- sur 2 ans</p>
            </div>
            <span class="flex items-end gap-2 font-sans text-5xl sm:text-7xl font-medium text-white/70 leading-none shrink-0 ml-6">20<i class="fas fa-percent text-lg sm:text-2xl mb-1"></i></span>
          </a>
          <a href="#deux-formules" (click)="scrollTo('deux-formules', $event); openFormula.set('revendeur')" class="flex items-end justify-between rounded-2xl bg-secondary-900 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] p-6 no-underline hover:bg-gray-900 hover:border-white/15 transition-colors group">
            <div class="text-left self-center">
              <p class="text-lg font-semibold text-white/80 group-hover:text-white transition-colors">Revendeur officiel</p>
              <p class="mt-1 text-sm text-white/40">min. CHF 825.- la 1<sup class="text-[0.6em]">re</sup> année</p>
            </div>
            <span class="flex items-end gap-2 font-sans text-5xl sm:text-7xl font-medium text-white/70 leading-none shrink-0 ml-6">50<i class="fas fa-percent text-lg sm:text-2xl mb-1"></i></span>
          </a>
        </div>
        <div class="mt-10 flex justify-center gap-3">
          <a href="#deux-formules" (click)="scrollTo('deux-formules', $event)"><app-button variant="primary" icon="arrow-right">Découvrir vos options</app-button></a>
          <a href="#pour-qui" (click)="scrollTo('pour-qui', $event)"><app-button variant="dark" [pill]="true">Pour qui ?</app-button></a>
        </div>
      </div>
    </app-page-section>

    <div class="pt-16 xl:pt-0"></div>

    <!-- Pour qui ? -->
    <app-page-section id="pour-qui">
      <div class="relative rounded-3xl px-4 py-14 sm:px-8 lg:px-14 lg:py-16 overflow-hidden" style="background: linear-gradient(to bottom, #f5f7f9 0%, #fafbfc 50%, #ffffff 100%)">
        <div class="text-center">
          <app-public-tag>pour qui ?</app-public-tag>
          <h2 class="mt-6">Ce programme est ouvert à tous</h2>
          <p class="mt-4 max-w-2xl mx-auto text-base">
            Pas besoin d'être un expert en logiciel. Si vous avez un réseau dans
            le milieu communal, associatif ou privé — vous avez déjà tout ce
            qu'il faut.
          </p>
        </div>
        <div class="mt-12 grid grid-cols-1 gap-8 sm:gap-x-14 sm:grid-cols-2 sm:max-w-[55%] sm:mx-auto auto-rows-fr">
          <app-public-info-card icon="building">
            <span title>Vous êtes déjà client de location-salles.ch</span>
            <span description>Vous connaissez la plateforme et avez des contacts dans d'autres communes ou associations — le point de départ idéal.</span>
          </app-public-info-card>
          <app-public-info-card icon="coins">
            <span title>Vous cherchez un revenu complémentaire</span>
            <span description>Indépendants, consultants ou agents communaux avec un bon réseau local — sans engagement ni compétence technique requise.</span>
          </app-public-info-card>
          <app-public-info-card icon="laptop-code">
            <span title>Vous revendez déjà des solutions logicielles</span>
            <span description>Agences web, intégrateurs ou revendeurs IT : un contrat formel avec des conditions adaptées à votre modèle.</span>
          </app-public-info-card>
          <app-public-info-card icon="map-marker-alt" badge="Recherche active">
            <span title>Vous êtes actif en Suisse&nbsp;alémanique</span>
            <span description>Une opportunité de terrain en Suisse alémanique avec peu de concurrence locale — nous cherchons activement des partenaires.</span>
          </app-public-info-card>
        </div>
        <div class="mt-10 flex justify-center">
          <a href="#contact" (click)="scrollTo('contact', $event)">
            <app-button variant="primary" icon="envelope">Nous contacter</app-button>
          </a>
        </div>
      </div>
    </app-page-section>

    <!-- Formules & rémunération -->
    <app-page-section id="deux-formules">
      <div class="grid grid-cols-1 gap-10 lg:grid-cols-[5fr_7fr] lg:items-start">

        <!-- Image portrait avec cross-fade -->
        <div class="relative aspect-[4/5] overflow-hidden rounded-3xl order-2 lg:order-1">
          <img
            src="assets/img/partners-apporteur.webp"
            alt="Apporteur d'affaire"
            class="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
            [style.opacity]="openFormula() === 'apporteur' ? 1 : 0"
          />
          <img
            src="assets/img/partners-revendeur.webp"
            alt="Revendeur officiel"
            class="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
            [style.opacity]="openFormula() === 'revendeur' ? 1 : 0"
          />
          <!-- Gradient fort en bas pour lisibilité du texte -->
          <div class="pointer-events-none absolute inset-0" style="background: linear-gradient(to top, rgba(12,18,28,0.95) 0%, rgba(12,18,28,0.75) 22%, rgba(12,18,28,0.15) 50%, transparent 70%)"></div>
          <!-- Caption -->
          <div class="absolute bottom-0 inset-x-0 p-7">
            @if (openFormula() === 'apporteur') {
              <p class="text-xs font-semibold uppercase tracking-widest text-white/50">Apporteur d'affaire</p>
              <p class="mt-1.5 text-sm leading-relaxed text-white/85">Zéro compétence technique requise. Vous mettez en contact, on s'occupe du reste.</p>
            }
            @if (openFormula() === 'revendeur') {
              <p class="text-xs font-semibold uppercase tracking-widest text-white/50">Revendeur officiel</p>
              <p class="mt-1.5 text-sm leading-relaxed text-white/85">Un partenariat complet avec contrat formel. Démos, formation, support — et 50% la 1<sup>re</sup> année.</p>
            }
          </div>
        </div>

        <!-- Accordion droite — style Preline minimal -->
        <div class="flex flex-col justify-center order-1 lg:order-2">
          <app-public-tag>les formules</app-public-tag>
          <h2 class="mt-5 text-3xl font-bold md:text-4xl">Comment ça fonctionne ?</h2>
          <p class="mt-4 text-base text-gray-500">
            Choisissez le niveau d'implication qui vous correspond — et touchez une commission transparente sur chaque client apporté.
          </p>

          <div class="mt-8 w-full space-y-3 lg:pr-14">

            <!-- Apporteur d'affaire -->
            <div class="rounded-2xl border border-gray-200 px-5 transition-colors duration-200" [class.bg-gray-50]="openFormula() === 'apporteur'">
              <button (click)="selectFormula('apporteur')" class="flex w-full flex-col py-5 text-left">
                <div class="flex w-full items-center justify-between gap-4">
                  <p class="text-lg font-semibold text-gray-800">Apporteur d'affaire</p>
                  <i class="far fa-chevron-down shrink-0 text-sm text-gray-800 transition-transform duration-200" [class.rotate-180]="openFormula() === 'apporteur'"></i>
                </div>
                @if (openFormula() === 'apporteur') {
                  <p class="mt-2 text-sm text-gray-500">Vous gagnez 20% du prix de l'abonnement les 2 premières années</p>
                }
              </button>
              @if (openFormula() === 'apporteur') {
                <div class="pb-6">
                  <div class="space-y-2.5">
                    @for (step of apporteurSteps; track step.num) {
                      <div class="flex items-start gap-3">
                        <i class="far fa-check mt-1 shrink-0 text-xs text-gray-400"></i>
                        <p class="text-sm text-gray-500">{{ step.label }}</p>
                      </div>
                    }
                  </div>
                  <div class="mt-7 space-y-1">
                    <p class="text-sm font-semibold text-gray-800 mb-2">Exemples de gains</p>
                    <p class="text-sm text-gray-500">Hôte avec 1 salle : <span class="font-medium text-gray-500">CHF 560.- sur 2 ans</span></p>
                    <p class="text-sm text-gray-500">Hôte avec 3 salles : <span class="font-medium text-gray-500">CHF 880.- sur 2 ans</span></p>
                  </div>
                </div>
              }
            </div>

            <!-- Revendeur officiel -->
            <div class="rounded-2xl border border-gray-200 px-5 transition-colors duration-200" [class.bg-gray-50]="openFormula() === 'revendeur'">
              <button (click)="selectFormula('revendeur')" class="flex w-full flex-col py-5 text-left">
                <div class="flex w-full items-center justify-between gap-4">
                  <p class="text-lg font-semibold text-gray-800">Revendeur officiel</p>
                  <i class="far fa-chevron-down shrink-0 text-sm text-gray-800 transition-transform duration-200" [class.rotate-180]="openFormula() === 'revendeur'"></i>
                </div>
                @if (openFormula() === 'revendeur') {
                  <p class="mt-2 text-sm text-gray-500">Vous gagnez 50% du prix de l'abonnement la 1<sup class="text-[0.6em]">re</sup> année, puis 20% jusqu'à résiliation</p>
                }
              </button>
              @if (openFormula() === 'revendeur') {
                <div class="pb-6">
                  <div class="space-y-2.5">
                    @for (step of revendeurSteps; track step.num) {
                      <div class="flex items-start gap-3">
                        <i class="far fa-check mt-1 shrink-0 text-xs text-gray-400"></i>
                        <p class="text-sm text-gray-500" [innerHTML]="step.label"></p>
                      </div>
                    }
                  </div>
                  <div class="mt-7 space-y-1">
                    <p class="text-sm font-semibold text-gray-800 mb-2">Exemples de gains</p>
                    <p class="text-sm text-gray-500">Hôte avec 1 salle : <span class="font-medium text-gray-500">CHF 825.- la 1<sup class="text-[0.6em]">re</sup> année, puis CHF 230.-/an</span></p>
                    <p class="text-sm text-gray-500">Hôte avec 3 salles : <span class="font-medium text-gray-500">CHF 1'225.- la 1<sup class="text-[0.6em]">re</sup> année, puis CHF 390.-/an</span></p>
                  </div>
                </div>
              }
            </div>

          </div>

          <div class="mt-8 flex flex-wrap gap-3 lg:pr-14">
            <a href="#contact" (click)="scrollTo('contact', $event)">
              <app-button variant="primary" icon="envelope">Nous contacter</app-button>
            </a>
            <a routerLink="/louer-ma-salle/tarifs">
              <app-button variant="outlined" icon="arrow-right">Tarifs des abonnements</app-button>
            </a>
          </div>
        </div>

      </div>
    </app-page-section>

    <!-- Contact -->
    <app-page-section id="contact" backgroundColor="secondary" [waves]="true" [noContainer]="true" padding="lg">
      <div class="container max-w-5xl">
        <div class="mx-auto max-w-2xl text-white">
          <app-public-tag>contact</app-public-tag>
          <h2 class="mt-4 text-white">Nous sommes à votre écoute !</h2>
          <p class="mt-4 text-base text-white/70">
            Contactez-nous pour plus de renseignements, pour discuter de votre
            profil partenaire ou pour une démo de notre logiciel. On répond
            rapidement.
          </p>
          <div class="mt-8 flex flex-wrap gap-3">
            <a href="mailto:contact@ercos.ch">
              <app-button variant="dark" icon="envelope">
                contact@ercos.ch
              </app-button>
            </a>
            <a href="tel:0215253060">
              <app-button variant="dark" icon="phone">
                021 525 30 60
              </app-button>
            </a>
          </div>
        </div>
      </div>
    </app-page-section>

    <div class="h-20 bg-white"></div>
  `,
  standalone: false,
})
export class PartnersResellersPageComponent {
  openFormula = signal<'apporteur' | 'revendeur'>('apporteur');

  selectFormula(formula: 'apporteur' | 'revendeur') {
    const other = formula === 'apporteur' ? 'revendeur' : 'apporteur';
    this.openFormula.set(this.openFormula() === formula ? other : formula);
  }

  scrollTo(id: string, event: Event) {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  apporteurSteps = [
    { num: 1, label: 'Vous identifiez un client potentiel dans votre réseau' },
    { num: 2, label: 'Vous nous le présentez et on prend en charge la démo' },
    { num: 3, label: "On s'occupe de la vente, de la formation et du support" },
    { num: 4, label: 'Vous touchez votre commission automatiquement' },
  ];

  revendeurSteps = [
    { num: 1, label: "Signature d'un contrat de partenariat revendeur" },
    { num: 2, label: 'Vous prospectez, organisez et réalisez les démos' },
    { num: 3, label: 'Vous assurez la formation et le support de 1er niveau' },
    { num: 4, label: 'Vous bénéficiez de 50% la 1<sup>re</sup> année, puis 20% à vie' },
  ];
}
